import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const API = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [authReady, setAuthReady] = useState(false); // true once token check is fully done
  const [error, setError]         = useState(null);
  const refreshTimer              = useRef(null);

  // ── Persist tokens in localStorage ──────────────────────────────────────
  const saveTokens = (accessToken, refreshToken) => {
    localStorage.setItem('fslp:access',  accessToken);
    localStorage.setItem('fslp:refresh', refreshToken);
  };
  const clearTokens = () => {
    localStorage.removeItem('fslp:access');
    localStorage.removeItem('fslp:refresh');
  };
  const getAccess  = () => localStorage.getItem('fslp:access');
  const getRefresh = () => localStorage.getItem('fslp:refresh');

  // ── Auto-refresh access token before it expires (every 6 days) ──────────
  const scheduleRefresh = useCallback(() => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    const MS = (7 * 24 * 60 * 60 - 60) * 1000;
    refreshTimer.current = setTimeout(refreshAccess, MS);
  }, []);

  const refreshAccess = useCallback(async () => {
    const rt = getRefresh();
    if (!rt) return logout();
    try {
      const res  = await fetch(`${API}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: rt }),
      });
      const data = await res.json();
      if (data.success) {
        saveTokens(data.accessToken, data.refreshToken);
        setUser(data.user);
        scheduleRefresh();
      } else {
        logout();
      }
    } catch {
      logout();
    }
  }, [scheduleRefresh]);

  // ── On mount: check for existing session ────────────────────────────────
  useEffect(() => {
    async function check() {
      const token = getAccess();
      if (!token) {
        setLoading(false);
        setAuthReady(true);
        return;
      }
      try {
        const res  = await fetch(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (data.success) {
          setUser(data.user);
          scheduleRefresh();
        } else if (data.code === 'TOKEN_EXPIRED') {
          await refreshAccess();
        } else {
          clearTokens();
        }
      } catch (err) {
        console.error('AUTH CHECK ERROR:', err);
        clearTokens();
      } finally {
        setLoading(false);
        setAuthReady(true); // auth check fully complete — safe to make API calls now
      }
    }
    check();
    return () => { if (refreshTimer.current) clearTimeout(refreshTimer.current); };
  }, []);

  // ── login ────────────────────────────────────────────────────────────────
  const login = useCallback(async (email, password) => {
    setError(null);
    const res  = await fetch(`${API}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.message || 'Login failed');
    saveTokens(data.accessToken, data.refreshToken);
    setUser(data.user);
    setAuthReady(true);
    scheduleRefresh();
    return data.user;
  }, [scheduleRefresh]);

  // ── loginWithTokens — used by AuthCallback after Google OAuth ───────────
  const loginWithTokens = useCallback(async (accessToken, refreshToken) => {
    saveTokens(accessToken, refreshToken);
    try {
      const res  = await fetch(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const data = await res.json();
      if (data.success) {
        setUser(data.user);
        scheduleRefresh();
      }
    } catch { /* ignore — tokens are saved, next mount will retry */ }
    setAuthReady(true); // mark ready even if /me failed — token is saved
  }, [scheduleRefresh]);

  // ── logout ───────────────────────────────────────────────────────────────
  const logout = useCallback(async () => {
    const token = getAccess();
    if (token) {
      try {
        await fetch(`${API}/auth/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch { /* ignore network errors on logout */ }
    }
    clearTokens();
    setUser(null);
    setAuthReady(false);
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
  }, []);

  // ── getAuthHeader ────────────────────────────────────────────────────────
  // NOTE: Do NOT include Content-Type here — multipart uploads need to set
  // their own boundary. Strip Content-Type in fetch calls that use FormData.
  const getAuthHeader = useCallback(() => ({
    Authorization: `Bearer ${getAccess() || ''}`,
    'Content-Type': 'application/json',
  }), []);

  // ── Role helpers ─────────────────────────────────────────────────────────
  const isAdmin   = user?.role === 'admin';
  const isTeacher = user?.role === 'teacher';
  const isParent  = user?.role === 'parent';
  const hasRole   = (...roles) => roles.includes(user?.role);

  return (
    <AuthContext.Provider value={{
      user, loading, authReady, error, setError,
      login, logout, loginWithTokens,
      isAdmin, isTeacher, isParent, hasRole,
      getAuthHeader,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
};